import React, { useState, useRef, useContext, useEffect } from "react";
import { UrlContext } from "../../contexts/UrlContext";
import Swal from "sweetalert2";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import { AnimatePresence, motion } from "framer-motion";
import Attachments from "./Attachments";
import scrollDesign from '../../components/EphProfile/MyProjectPage.module.css';
import { de } from "date-fns/locale";
import { AppContext } from "../../contexts/AppContext";

const ComposeNewEmail = ({ onClose, onSend = null, defaultTo = [], defaultCc = [], defaultBcc = [], defaultSubject = "", defaultBody = "", quotedEmail = null, defaultTemplates = [], setSuccess = () => {}, logFunction = null, callingFrom = null, projectID = null, }) => {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [toRecipients, setToRecipients] = useState(defaultTo);
  const [from, setFrom] = useState([]);
  const [bcc, setBcc] = useState(defaultBcc);
  const [cc, setCc] = useState(defaultCc);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [hasAttachment, setHasAttachment] = useState(false);
  const [openDragDrop, setOpenDragDrop] = useState(false);
  const [attachmentModalSource, setAttachmentModalSource] = useState("upload");
  const [showCc, setShowCc] = useState((defaultCc || []).length > 0);
  const [showBcc, setShowBcc] = useState((defaultBcc || []).length > 0);
  const [signatureHtml, setSignatureHtml] = useState("");
  const bodyRef = useRef(null);
  const bodyDragDepthRef = useRef(0);
  const signatureRef = useRef(null);
  const { API_URL } = useContext(UrlContext);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedLang, setSelectedLang] = useState("");
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
  const [creatingNewExisting, setCreatingNewExisting] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [filteredTemplates, setFilterTemplates] = useState(defaultTemplates);
  const [tempLang, setTemplang] = useState('');
  const { allEmails } = useContext(AppContext);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Block body scroll when modal is open
  useEffect(() => {
    document.body.classList.add("overflow-hidden");
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  useEffect(() => {
    adjustSignature();
  }, [signatureHtml, includeSignature]);

  const getToken = () => {
    const tokenObj = localStorage.getItem("eph-token");
    return JSON.parse(tokenObj)?.value || "";
  };

  const adjustSignature = () => {
    if (signatureRef.current) {
      const imgs = signatureRef.current.querySelectorAll("img");
      const lastThreeImgs = Array.from(imgs).slice(-3);
      lastThreeImgs.forEach((img, index) => {
        img.style.width = "auto";
        img.style.height = "auto";
        img.style.maxWidth = index !== 1 ? "100%" : "95px";
        img.style.maxHeight = index !== 1 ? "1000px" : "150px";
        if (index === 1) img.style.marginLeft = "2px";
      });
    }
  };

  useEffect(() => {
    if (defaultTemplates.length > 0) {
      const idx = defaultTemplates.findIndex(t => t.isDefault);
      const selectedIdx = idx !== -1 ? idx : 0;
      const userDefault = defaultTemplates[selectedIdx];
      setBody(userDefault.value);
      setSelectedLang(userDefault.label);
      setSelectedTemplateIndex(selectedIdx);
      if (!defaultBody && userDefault.subject) {
        setSubject(userDefault.subject);
      }
    }
  }, [defaultTemplates]);

  // Function to filter language on language changes
  const switchingLanguage = async(lang) => {
    if (lang === 'en' && defaultTemplates.length > 0) {
      const onlyEnglish = defaultTemplates.filter((t) => t.lang === 'en')
      const idx = onlyEnglish.findIndex(t => t.isDefault);
      const defaultIndex = idx !== -1 ? idx : 0;
      const userDefaultTemp = onlyEnglish[defaultIndex];
      setFilterTemplates(onlyEnglish);
      setBody(userDefaultTemp.value);
      setSelectedLang(userDefaultTemp.label);
      setSelectedTemplateIndex(defaultIndex);
      if (!defaultBody && userDefaultTemp.subject) {
        setSubject(userDefaultTemp.subject);
      }
    } else if (lang === 'de' && defaultTemplates.length > 0) {
      const onlyGerman = defaultTemplates.filter((t) => t.lang === 'de')
      const idx = onlyGerman.findIndex(t => t.isDefault);
      const defaultIndex = idx !== -1 ? idx : 0;
      const userDefaultTemp = onlyGerman[defaultIndex];
      setFilterTemplates(onlyGerman);
      setBody(userDefaultTemp.value);
      setSelectedLang(userDefaultTemp.label);
      setSelectedTemplateIndex(defaultIndex);
      if (!defaultBody && userDefaultTemp.subject) {
        setSubject(userDefaultTemp.subject);
      }
    } else {
      const idx = defaultTemplates.findIndex(t => t.isDefault);
      const selectedIdx = idx !== -1 ? idx : 0;
      const userDefault = defaultTemplates[selectedIdx];
      setFilterTemplates(defaultTemplates);
      setBody(userDefault.value);
      setSelectedLang(userDefault.label);
      setSelectedTemplateIndex(selectedIdx);
      if (!defaultBody && userDefault.subject) {
        setSubject(userDefault.subject);
      }
    }
  };

  // handle language selection
  const handleLanguageChoice = async(lang) => {
    setTemplang(lang);
    await switchingLanguage(lang);
  };

  // handle template change
  const handleTemplateChange = (index) => {
    setSelectedTemplateIndex(index);
    setSelectedLang(filteredTemplates[index].label);
    setBody(filteredTemplates[index].value);
    if (!defaultBody && filteredTemplates[index].subject) {
      setSubject(filteredTemplates[index].subject);
    }
  };

  // const handleTemplateChange = (index) => {
  //   setSelectedTemplateIndex(index);
  //   setSelectedLang(defaultTemplates[index].label);
  //   setBody(defaultTemplates[index].value);
  //   if (!defaultBody && defaultTemplates[index].subject) {
  //     setSubject(defaultTemplates[index].subject);
  //   }
  // };

  // Default API to send emails
  const sendEmailViaGraph = async (emailData) => {
    const accessToken = localStorage.getItem("accesstoken");
    try {
      const response = await fetch(`${API_URL}send-eph-email-via-graph`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken,
          to: emailData.to || [],
          cc_email: emailData.cc_email || [],
          bcc_email: emailData.bcc_email || [],
          subject: emailData.subject || "",
          body: emailData.body || "",
          attachment: emailData.attachment || [],
        }),
      });
    
      if (response.status === 200) {
        const result = await response.json();
        if (result.status === "success") {
          setSuccess(true);
          setTimeout(() => {
            onClose(); // <-- delay close slightly
          }, 1000);
          Swal.fire("Success", "Email sent successfully!", "success");
          return true;
        } else {
          setSuccess(false);
          Swal.fire("Error", result.text || "Failed to send email!", "error");
          return false;
        }
      } else {
        setSuccess(false);
        const errorData = await response.json();
        Swal.fire("Error", errorData.text || "Failed to send email!", "error");
        return false;
      }
    } catch (error) {
      setSuccess(false);
      Swal.fire("Error", "Network error while sending email!", "error");
      return false;
    }
  }; 

  const handleSend = async () => {
    if (isSendingEmail || !toRecipients?.length) return;
    setIsSendingEmail(true);
    let fullBody = body;

    const template =
      selectedTemplateIndex !== null && defaultTemplates[selectedTemplateIndex]
        ? defaultTemplates[selectedTemplateIndex]
        : null;

    if (template?.linkedButton) {
      fullBody = fullBody.replace('{{BUTTON_DO_NOT_REMOVE}}', template.linkedButton);
    }

    if (includeSignature) {
      fullBody = `${fullBody}<br>${signatureHtml}`;
    }

    const payload = {
      to: toRecipients,
      from,
      bcc_email: bcc,
      cc_email: cc,
      subject,
      body: fullBody,
      enableSignature: includeSignature,
      attachment: attachments,
    };

    try {
      if (onSend && typeof onSend === "function") {
        await Promise.resolve(onSend(payload));
        if (logFunction && typeof logFunction === "function") {
          logFunction({
            sendTo: toRecipients,
            ccMail: cc,
          });
        }
        setIsSendingEmail(false);
      } else {
        const sentSuccessfully = await sendEmailViaGraph(payload);
        if (logFunction && typeof logFunction === "function") {
          logFunction({
            sendTo: toRecipients,
            ccMail: cc,
          });
        }

        if (!sentSuccessfully) {
          setIsSendingEmail(false);
        }
      }
    } catch (error) {
      setIsSendingEmail(false);
      Swal.fire("Error", "Unexpected error while sending email!", "error");
    }
  };

  const removeAttachment = (index) => {
    const updated = [...attachments];
    updated.splice(index, 1);
    setAttachments(updated);
    if (updated.length === 0) setHasAttachment(false);
  };

  const fetchSignature = async () => {
    const tokenObj = localStorage.getItem("eph-token");
    if (!tokenObj) return;

    const token = JSON.parse(tokenObj).value;
    try {
      const res = await fetch(`${API_URL}account-settings`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      });
      const data = await res.json();
      const signature = data.signature
        .replace("{{name}}", `${data.user.sigtitle} ${data.user.sigfirstname} ${data.user.siglastname}`)
        .replace("{{designation}}", data.user.config)
        .replace("{{contactNumber}}", data.user.path_z);

      setSignatureHtml(signature);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchSignature();
  }, []);

  useEffect(() => {
    if (quotedEmail) {
      const quoted = generateQuotedHtml(quotedEmail);
      setBody((prev) => `${prev}<br>${quoted}`);
    }
  }, [quotedEmail]);
  
  const Block = Quill.import("blots/block");
  Block.tagName = "div";
  Quill.register(Block, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "arial",
    "georgia",
    "times-new-roman",
    "courier-new",
    "verdana",
    "tahoma",
    "lucida",
  ];
  Quill.register(Font, true);

  const generateQuotedHtml = (original) => {
    const sender = original.from?.emailAddress?.name || original.from?.emailAddress?.address;
    const date = new Date(original.receivedDateTime).toLocaleString();
    const subjectLine = original.subject || "";
    const content = original.body?.content || "";

    return `
      <br><br><br>
      <div style="border-left: 2px solid gray; border-top: 1px solid lightgray; padding-left: 10px; margin-top: 20px; padding-top: 10px;">
        <p style="color: gray; font-size: 13px; margin-bottom: 8px;">
          On ${date}, ${sender} wrote:
        </p>
        ${content}
      </div>
    `;
  };

  const createNewFromExisting = async () => {
    setCreatingNewExisting(true);
    const token = getToken();
    let updatedBody = body;
    updatedBody = updatedBody.replace(/Dear\s+([A-Za-zÀ-ÿ'’\-\.]+\s*)+,/i, "Dear first_name last_name,");

    const requestBody = {
      projectID: projectID,
      token: token,
      templateType: callingFrom,
      subject: subject,
      body: updatedBody,
      language: selectedLang,
    };
    try {
      const response = await fetch(
        `${API_URL}create-new-from-existing-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();
      if (responseData.status === "success") {
        setTemplateSaved(true);
        setTimeout(() => {
          setTemplateSaved(false);
        }, 2000);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to save as template",
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unexpected error while saving as template",
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setCreatingNewExisting(false);
    }
  };



  const emailPattern = /^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/;
  const [recipientInputs, setRecipientInputs] = useState({ to: "", cc: "", bcc: "" });
  const [recipientErrors, setRecipientErrors] = useState({ to: "", cc: "", bcc: "" });
  const [focusedRecipientField, setFocusedRecipientField] = useState(null);
  const [hoveredRecipientField, setHoveredRecipientField] = useState(null);
  const [isBodyDragActive, setIsBodyDragActive] = useState(false);

  useEffect(() => {
    const resetBodyDragState = () => {
      bodyDragDepthRef.current = 0;
      setIsBodyDragActive(false);
    };

    const handleWindowDragLeave = (event) => {
      const leftWindow =
        event.clientX <= 0 ||
        event.clientY <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY >= window.innerHeight;

      if (leftWindow) {
        resetBodyDragState();
      }
    };

    window.addEventListener("drop", resetBodyDragState);
    window.addEventListener("dragend", resetBodyDragState);
    window.addEventListener("dragleave", handleWindowDragLeave);

    return () => {
      window.removeEventListener("drop", resetBodyDragState);
      window.removeEventListener("dragend", resetBodyDragState);
      window.removeEventListener("dragleave", handleWindowDragLeave);
    };
  }, []);

  const trimEmail = (value = "") => String(value || "").trim();

  const isValidEmailAddress = (value = "") => {
    const trimmed = trimEmail(value);
    return emailPattern.test(trimmed);
  };

  const splitEmailInput = (value = "") =>
    String(value || "")
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const getRecipientState = (field) => {
    if (field === "to") return toRecipients;
    if (field === "cc") return cc;
    return bcc;
  };

  const setRecipientState = (field, updater) => {
    if (field === "to") {
      setToRecipients((prev) => updater(prev || []));
    } else if (field === "cc") {
      setCc((prev) => updater(prev || []));
    } else {
      setBcc((prev) => updater(prev || []));
    }
  };

  const allRecipientEmails = () => [
    ...(toRecipients || []),
    ...(cc || []),
    ...(bcc || []),
  ];

  const addEmailsToField = (field, rawValue) => {
    const cleanedValue = String(rawValue || "").trim();
    const candidates = splitEmailInput(cleanedValue).map(trimEmail);

    if (!candidates.length) return false;

    const invalidCandidates = candidates.filter((email) => !isValidEmailAddress(email));
    if (invalidCandidates.length > 0) {
      setRecipientErrors((prev) => ({
        ...prev,
        [field]: `Invalid email: ${invalidCandidates[0]}`,
      }));
      return false;
    }

    const existing = new Set(allRecipientEmails().map((email) => String(email).toLowerCase()));
    const uniqueCandidates = [];

    candidates.forEach((email) => {
      const key = email.toLowerCase();
      if (!existing.has(key)) {
        existing.add(key);
        uniqueCandidates.push(email);
      }
    });

    if (!uniqueCandidates.length) {
      setRecipientErrors((prev) => ({ ...prev, [field]: "This email is already added." }));
      return false;
    }

    setRecipientState(field, (prev) => [...(prev || []), ...uniqueCandidates]);
    setRecipientErrors((prev) => ({ ...prev, [field]: "" }));
    return true;
  };

  const removeEmailFromField = (field, emailToRemove) => {
    setRecipientState(field, (prev) =>
      (prev || []).filter(
        (email) => String(email).toLowerCase() !== String(emailToRemove).toLowerCase()
      )
    );
  };

  const moveEmailBetweenFields = (sourceField, targetField, email) => {
    const cleanEmail = trimEmail(email);
    if (!cleanEmail || sourceField === targetField) return;

    removeEmailFromField(sourceField, cleanEmail);
    const targetEmails = getRecipientState(targetField) || [];
    const alreadyExists = targetEmails.some(
      (targetEmail) => String(targetEmail).toLowerCase() === cleanEmail.toLowerCase()
    );

    if (!alreadyExists) {
      setRecipientState(targetField, (prev) => [...(prev || []), cleanEmail]);
    }
  };

  const handleRecipientInputChange = (field, value) => {
    setRecipientInputs((prev) => ({ ...prev, [field]: value }));
    if (recipientErrors[field]) {
      setRecipientErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const commitRecipientInput = (field) => {
    const value = recipientInputs[field];
    if (!value?.trim()) return;

    const added = addEmailsToField(field, value);
    if (added) {
      setRecipientInputs((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const fileToAttachmentPayload = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve({
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
          base64,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const addDroppedFilesAsAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    try {
      const newAttachments = await Promise.all(files.map(fileToAttachmentPayload));
      setAttachments((prev) => [...(prev || []), ...newAttachments]);
      setHasAttachment(true);
    } catch (error) {
      Swal.fire("Error", "Could not attach one or more files.", "error");
    }
  };

  const isFileDragEvent = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const getEmailInitial = (email = "") => {
    const cleanEmail = String(email || "").trim();
    return cleanEmail.charAt(0)?.toUpperCase() || "?";
  };

  const emailAvatarClasses = [
    "bg-[#365561]",
    "bg-slate-600",
    "bg-cyan-700",
    "bg-emerald-700",
    "bg-indigo-700",
    "bg-rose-700",
    "bg-amber-700",
    "bg-violet-700",
  ];

  const getEmailAvatarClass = (email = "") => {
    const cleanEmail = String(email || "");
    const total = cleanEmail
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);

    return emailAvatarClasses[total % emailAvatarClasses.length];
  };

  const getSuggestionProfilePicture = (contact = {}) => {
    const picture = contact.profilePicture;

    if (!picture) return null;

    return String(picture).includes("/pictures/")
      ? `https://images.expertpowerhouse.com${picture}`
      : picture;
  };

  const getEmailSuggestionInitial = (contact = {}) => {
    const displayValue = contact.name || contact.email || "";
    return String(displayValue).trim().charAt(0)?.toUpperCase() || "?";
  };

  const getFilteredEmailSuggestions = (field) => {
    const query = String(recipientInputs[field] || "").trim().toLowerCase();

    if (query.length < 2) return [];

    const existingEmails = new Set(
      allRecipientEmails().map((email) => String(email).toLowerCase())
    );

    return (allEmails || [])
      .filter((contact) => {
        const email = trimEmail(contact?.email);
        const name = String(contact?.name || "").trim();

        if (!email || existingEmails.has(email.toLowerCase())) return false;
        if (!isValidEmailAddress(email)) return false;

        const searchableText = `${name} ${email}`.toLowerCase();
        return searchableText.includes(query);
      })
      .slice(0, 8);
  };

  const addSuggestedEmailToField = (field, contact) => {
    const email = trimEmail(contact?.email);

    if (!email) return;

    const added = addEmailsToField(field, email);

    if (added) {
      setRecipientInputs((prev) => ({ ...prev, [field]: "" }));
      setRecipientErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const renderRecipientField = ({ field, label, emails, placeholder }) => {
    const hasEmails = (emails || []).length > 0;
    const isFocused = focusedRecipientField === field;
    const isHovering = hoveredRecipientField === field;
    const shouldShowLabel = isFocused || (isHovering && hasEmails) || Boolean(recipientInputs[field]);
    const emailSuggestions = getFilteredEmailSuggestions(field);
    const shouldShowSuggestions =
      isFocused && Boolean(recipientInputs[field]?.trim()) && emailSuggestions.length > 0;

    return (
      <div
        className="border-b border-slate-200 px-3 py-2 sm:px-4"
        onMouseEnter={() => setHoveredRecipientField(field)}
        onMouseLeave={() =>
          setHoveredRecipientField((prev) => (prev === field ? null : prev))
        }
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          try {
            const payload = JSON.parse(e.dataTransfer.getData("application/json"));
            if (payload?.email && payload?.sourceField) {
              moveEmailBetweenFields(payload.sourceField, field, payload.email);
            }
          } catch (error) {
            const droppedEmail = e.dataTransfer.getData("text/plain");
            addEmailsToField(field, droppedEmail);
          }
        }}
      >
        <div
          className={[
            "grid min-w-0 items-start gap-2",
            shouldShowLabel
              ? "grid-cols-[18px_minmax(0,1fr)] sm:grid-cols-[18px_minmax(0,1fr)]"
              : "grid-cols-1",
          ].join(" ")}
        >
          {shouldShowLabel && (
            <div className="pt-1 text-[13px] font-semibold text-slate-500 sm:text-sm">
              {label}
            </div>
          )}

          <div className="relative min-w-0">
            <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center">
              {(emails || []).map((email) => (
                <span
                  key={`${field}-${email}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "application/json",
                      JSON.stringify({ email, sourceField: field })
                    );
                    e.dataTransfer.setData("text/plain", email);
                  }}
                  className="group inline-flex h-7 w-full max-w-full cursor-grab items-center overflow-hidden rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 transition hover:bg-slate-50 hover:shadow-sm active:cursor-grabbing sm:w-auto sm:max-w-[300px] sm:text-[13px]"
                  title="Drag to To, Cc, or Bcc"
                >
                  <span
                    className={[
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ml-[1px]",
                      getEmailAvatarClass(email),
                    ].join(" ")}
                  >
                    {getEmailInitial(email)}
                  </span>
                  <span className="min-w-0 flex-1 truncate px-1 sm:max-w-[220px] md:max-w-[260px]">
                    {email}
                  </span>
                  <button
                    type="button"
                    className="flex h-6 w-6 shrink-0 items-center justify-center text-slate-400 transition hover:text-red-500 focus:outline-none"
                    onClick={() => removeEmailFromField(field, email)}
                    aria-label={`Remove ${email}`}
                  >
                    <i className="fa-solid fa-xmark text-[10px]" />
                  </button>
                </span>
              ))}

              <input
                value={recipientInputs[field] || ""}
                onChange={(e) => handleRecipientInputChange(field, e.target.value)}
                onFocus={() => setFocusedRecipientField(field)}
                onKeyDown={(e) => {
                  if (["Enter", "Tab", ",", ";"].includes(e.key)) {
                    if (recipientInputs[field]?.trim()) {
                      e.preventDefault();
                      commitRecipientInput(field);
                    }
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData("text");
                  if (splitEmailInput(pasted).length > 1) {
                    e.preventDefault();
                    const added = addEmailsToField(field, pasted);
                    setRecipientInputs((prev) => ({
                      ...prev,
                      [field]: added ? "" : pasted.trim(),
                    }));
                  }
                }}
                onBlur={() => {
                  commitRecipientInput(field);
                  setFocusedRecipientField((prev) => (prev === field ? null : prev));
                }}
                className={[
                  "min-w-0 flex-1 border-0 bg-transparent px-0 py-1 text-sm outline-none placeholder:text-slate-400 sm:min-w-[170px]",
                  "basis-auto sm:basis-[180px]",
                  recipientErrors[field] ? "text-red-600" : "text-slate-800",
                ].join(" ")}
                placeholder={hasEmails ? "" : placeholder}
              />
            </div>

            <AnimatePresence>
              {shouldShowSuggestions && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
                >
                  {emailSuggestions.map((contact) => {
                    const suggestionPicture = getSuggestionProfilePicture(contact);

                    return (
                      <button
                        key={`${field}-suggestion-${contact.email}`}
                        type="button"
                        className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addSuggestedEmailToField(field, contact);
                        }}
                      >
                        {suggestionPicture ? (
                          <img
                            src={suggestionPicture}
                            alt=""
                            className="h-8 w-8 shrink-0 rounded-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span
                            className={[
                              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white",
                              getEmailAvatarClass(contact.email),
                            ].join(" ")}
                          >
                            {getEmailSuggestionInitial(contact)}
                          </span>
                        )}

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-800">
                            {contact.name || contact.email}
                          </span>
                          <span className="block truncate text-xs text-slate-500">
                            {contact.email}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            {recipientErrors[field] && (
              <p className="mt-1 text-xs font-medium text-red-500">
                {recipientErrors[field]}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed top-[70px] bottom-0 left-0 right-0 z-50 flex items-center justify-center px-2 py-3 cursor-default sm:px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="absolute -top-2 inset-0 bg-black/45 backdrop-blur-sm" />

        <motion.div
          layout
          initial={{ y: 18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 18, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="relative flex max-h-[calc(100vh-104px)] min-h-[400px] w-full max-w-[1120px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_22px_70px_rgba(15,23,42,0.28)] cursor-default"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-4">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="shrink-0 whitespace-nowrap text-[15px] font-semibold text-slate-900 sm:text-base">
                EPH Email
              </span>

              {defaultTemplates.length > 0 && (
                <>
                  <span className="shrink-0 text-slate-300">|</span>
                  <select
                    value={selectedTemplateIndex ?? ""}
                    onChange={(e) => handleTemplateChange(parseInt(e.target.value))}
                    className="min-w-0 flex-1 truncate rounded-lg border-0 bg-transparent px-1 py-1 text-sm font-medium text-slate-600 outline-none hover:bg-slate-50 focus:bg-slate-50 sm:max-w-[420px]"
                    title={
                      selectedTemplateIndex !== null && filteredTemplates[selectedTemplateIndex]
                        ? filteredTemplates[selectedTemplateIndex].name
                        : "Select template"
                    }
                  >
                    {filteredTemplates.map((tpl, i) => (
                      <option key={`${tpl.lang || "template"}-${i}`} value={i}>
                        {tpl.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              {callingFrom && (
                <button
                  type="button"
                  className="group relative inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-[#365561] focus:outline-none"
                  onClick={() => createNewFromExisting()}
                  aria-label="Save as template"
                >
                  {creatingNewExisting ? (
                    <i className="fa-solid fa-spinner fa-spin text-[#365561]" />
                  ) : templateSaved ? (
                    <i className="fa-solid fa-circle-check fa-shake text-green-500" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8A2 2 0 0 1 21 8.8V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                      <path d="M17 21v-7H7v7" />
                      <path d="M7 3v5h8" />
                    </svg>
                  )}
                  <span className="pointer-events-none absolute rounded-lg right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                    Save as template
                  </span>
                </button>
              )}

              <button
                type="button"
                className="group relative inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-red-500 focus:outline-none"
                onClick={onClose}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="19"
                  height="19"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
                <span className="pointer-events-none absolute rounded-lg right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                  Close
                </span>
              </button>
            </div>
          </div>
          <div className="shrink-0 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 sm:px-4">
              <button
                type="button"
                onClick={handleSend}
                disabled={!toRecipients?.length || isSendingEmail}
                className="inline-flex items-center gap-2 rounded-full bg-[#365561] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] disabled:cursor-not-allowed disabled:opacity-45 focus:outline-none"
              >
                {isSendingEmail ? (
                  <i className="fa-solid fa-spinner fa-spin" />
                ) : (
                  <i className="fa-solid fa-paper-plane" />
                )}
                <span>{isSendingEmail ? "Sending..." : "Send"}</span>
              </button>

              <div className="ml-auto flex items-center gap-2 mr-1">
                <button
                  type="button"
                  onClick={() => setShowCc((prev) => !prev)}
                  className={[
                    "group relative border-b-2 px-1 text-xs font-semibold text-slate-500 transition hover:text-[#365561] focus:outline-none",
                    showCc ? "border-[#365561] text-[#365561]" : "border-transparent",
                  ].join(" ")}
                  aria-label={showCc ? "Hide Cc" : "Open Cc"}
                >
                  Cc
                  <span className="pointer-events-none absolute rounded-lg right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                    {showCc ? "Hide Cc" : "Open Cc"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowBcc((prev) => !prev)}
                  className={[
                    "group relative border-b-2 px-1 text-xs font-semibold text-slate-500 transition hover:text-[#365561] focus:outline-none",
                    showBcc ? "border-[#365561] text-[#365561]" : "border-transparent",
                  ].join(" ")}
                  aria-label={showBcc ? "Hide Bcc" : "Open Bcc"}
                >
                  Bcc
                  <span className="pointer-events-none absolute rounded-lg right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                    {showBcc ? "Hide Bcc" : "Open Bcc"}
                  </span>
                </button>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentModalSource("upload");
                      setOpenDragDrop(true);
                    }}
                    className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full !border-0 !bg-transparent !text-slate-500 transition hover:!bg-slate-100 hover:!text-[#365561] focus:outline-none"
                    aria-label="Attach files from computer"
                    title="Attach files"
                  >
                    <i className="fa-solid fa-paperclip text-sm" />
                    {hasAttachment && (
                      <span className="absolute -right-0.5 -top-1 text-[10px] font-bold leading-none text-[#365561]">
                        {attachments.length}
                      </span>
                    )}
                    <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                      Attach from computer
                    </span>
                  </button>

                  <button
                    type="button"
                    disabled={!projectID}
                    onClick={() => {
                      setAttachmentModalSource("sharepoint");
                      setOpenDragDrop(true);
                    }}
                    className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full !border-0 !bg-transparent !text-slate-500 transition hover:!bg-slate-100 hover:!text-[#365561] disabled:cursor-not-allowed disabled:!text-slate-300 disabled:hover:!bg-transparent focus:outline-none"
                    aria-label="Attach project files from SharePoint"
                    title={projectID ? "Project files" : "Project files require a project"}
                  >
                    <i className="fa-solid fa-folder-open text-sm" />
                    <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                      {projectID ? "Project files" : "No project selected"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white">
              {renderRecipientField({
                field: "to",
                label: "To",
                emails: toRecipients,
                placeholder: "Add recipients",
              })}
              {showCc &&
                renderRecipientField({
                  field: "cc",
                  label: "Cc",
                  emails: cc,
                  placeholder: "Add cc recipients",
                })}
              {showBcc &&
                renderRecipientField({
                  field: "bcc",
                  label: "Bcc",
                  emails: bcc,
                  placeholder: "Add bcc recipients",
                })}

              <div className="border-b border-slate-200 px-3 py-2 sm:px-4">
                <input
                  className="w-full min-w-0 border-0 bg-transparent px-0 py-1 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                />
              </div>
            </div>

            {attachments.length > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 sm:px-4">
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, idx) => {
                    const blobUrl = URL.createObjectURL(
                      new Blob([Uint8Array.from(atob(att.base64), c => c.charCodeAt(0))], { type: att.contentType })
                    );
                    const fileType = att.contentType;
                    let icon = "fa-file";

                    if (fileType.includes("image")) icon = "fa-file-image";
                    else if (fileType.includes("pdf")) icon = "fa-file-pdf";
                    else if (fileType.includes("word")) icon = "fa-file-word";
                    else if (fileType.includes("excel")) icon = "fa-file-excel";
                    else if (fileType.includes("zip")) icon = "fa-file-zipper";
                    else if (fileType.includes("presentation") || fileType.includes("ppt")) icon = "fa-file-powerpoint";

                    return (
                      <div key={idx} className="flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 shadow-sm sm:max-w-[320px]">
                        {att.contentType.startsWith("image/") ? (
                          <img src={blobUrl} alt={att.name} className="h-9 w-9 shrink-0 rounded-lg object-cover" />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                            <i className={`fa-solid ${icon}`} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-semibold text-slate-800">{att.name}</div>
                          <div className="text-[11px] text-slate-500">{Math.round(att.size / 1024)} KB • {att.contentType.split("/")[1]}</div>
                        </div>
                        <button type="button" onClick={() => removeAttachment(idx)} className="rounded-full h-7 w-7 text-red-500 hover:bg-red-50 hover:text-red-700 focus:outline-none">
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div
            className={`${scrollDesign.scrollbarOverlay} relative flex-1 overflow-y-auto bg-white px-3 py-3 sm:px-4 sm:py-4`}
            onDragEnter={(e) => {
              if (isFileDragEvent(e)) {
                e.preventDefault();
                bodyDragDepthRef.current += 1;
                setIsBodyDragActive(true);
              }
            }}
            onDragOver={(e) => {
              if (isFileDragEvent(e)) {
                e.preventDefault();
                if (!isBodyDragActive) {
                  setIsBodyDragActive(true);
                }
              }
            }}
            onDragLeave={(e) => {
              if (isFileDragEvent(e)) {
                e.preventDefault();
                bodyDragDepthRef.current = Math.max(0, bodyDragDepthRef.current - 1);

                const nextTarget = e.relatedTarget;
                const leftDropArea =
                  !nextTarget || !e.currentTarget.contains(nextTarget);

                if (bodyDragDepthRef.current === 0 || leftDropArea) {
                  bodyDragDepthRef.current = 0;
                  setIsBodyDragActive(false);
                }
              }
            }}
            onDrop={async (e) => {
              if (isFileDragEvent(e)) {
                e.preventDefault();
                bodyDragDepthRef.current = 0;
                setIsBodyDragActive(false);
                await addDroppedFilesAsAttachments(e.dataTransfer.files);
              }
            }}
          >
            <AnimatePresence>
              {isBodyDragActive && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="pointer-events-none absolute inset-3 z-40 flex items-center justify-center rounded-2xl border-2 border-dashed border-[#365561]/60 bg-white/75 text-[#365561] backdrop-blur-sm sm:inset-4"
                >
                  <div className="text-center">
                    <i className="fa-solid fa-cloud-arrow-up mb-2 text-3xl" />
                    <p className="text-sm font-semibold">Drop files here to attach them</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex-container-emil" style={{ display: "flex", flexDirection: "column" }}>
              <div className="flex-item-email-body">
                <div className="compose-email-editor overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <ReactQuill
                    theme="snow"
                    value={body}
                    onChange={setBody}
                    modules={{
                      toolbar: [
                        [{ font: Font.whitelist }],
                        [{ header: [1, 2, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["link"],
                      ],
                    }}
                    formats={["font", "header", "bold", "italic", "underline", "strike", "list", "bullet", "link"]}
                    style={{ overflowY: "auto", display: "flex", flexDirection: "column" }}
                  />
                  <style>
                    {`
                      .compose-email-editor .ql-editor {
                        font-family: Arial, sans-serif;
                        font-size: 14px;
                        line-height: 1.45;
                        min-height: 220px;
                        padding: 14px 16px !important;
                      }

                      .compose-email-editor .ql-container {
                        height: auto !important;
                        min-height: 220px;
                        border: 0 !important;
                        padding-bottom: 10px;
                      }

                      .compose-email-editor .ql-container .ql-snow .ql-hidden {
                        display: none !important;
                      }

                      .compose-email-editor .ql-toolbar.ql-snow {
                        position: inherit;
                        top: 0;
                        right: 24px;
                        padding: 6px 8px;
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px;
                        border: 0 !important;
                        border-bottom: 1px solid rgb(226 232 240) !important;
                        background: rgb(248 250 252);
                      }

                      .ql-font-arial { font-family: Arial, sans-serif; }
                      .ql-font-georgia { font-family: Georgia, serif; }
                      .ql-font-times-new-roman { font-family: "Times New Roman", serif; }
                      .ql-font-courier-new { font-family: "Courier New", monospace; }
                      .ql-font-verdana { font-family: Verdana, sans-serif; }
                      .ql-font-tahoma { font-family: Tahoma, sans-serif; }
                      .ql-font-lucida { font-family: "Lucida Sans Unicode", sans-serif; }
                      .ql-font-roboto { font-family: "Roboto", sans-serif; }
                      .ql-font-lato { font-family: "Lato", sans-serif; }

                      .ql-snow .ql-picker.ql-font .ql-picker-label::before,
                      .ql-snow .ql-picker.ql-font .ql-picker-item::before {
                        content: attr(data-value);
                        font-family: inherit;
                      }

                      .ql-picker-item[data-value="arial"]::before,
                      .ql-picker-label[data-value="arial"]::before {
                        font-family: Arial, sans-serif;
                        content: "Arial";
                      }

                      .ql-picker-item[data-value="georgia"]::before,
                      .ql-picker-label[data-value="georgia"]::before {
                        font-family: Georgia, serif;
                        content: "Georgia";
                      }

                      .ql-picker-item[data-value="times-new-roman"]::before,
                      .ql-picker-label[data-value="times-new-roman"]::before {
                        font-family: "Times New Roman", serif;
                        content: "Times New Roman";
                      }

                      .ql-picker-item[data-value="courier-new"]::before,
                      .ql-picker-label[data-value="courier-new"]::before {
                        font-family: "Courier New", monospace;
                        content: "Courier New";
                      }

                      .ql-picker-item[data-value="verdana"]::before,
                      .ql-picker-label[data-value="verdana"]::before {
                        font-family: Verdana, sans-serif;
                        content: "Verdana";
                      }

                      .ql-picker-item[data-value="tahoma"]::before,
                      .ql-picker-label[data-value="tahoma"]::before {
                        font-family: Tahoma, sans-serif;
                        content: "Tahoma";
                      }

                      .ql-picker-item[data-value="lucida"]::before,
                      .ql-picker-label[data-value="lucida"]::before {
                        font-family: "Lucida Sans Unicode", sans-serif;
                        content: "Lucida";
                      }

                      .ql-editor ul {
                        padding-left: 1.25em;
                        margin: 0.5em 0;
                      } 

                      .compose-email-editor .ql-container {
                        position: relative;
                      }

                      .compose-email-editor .ql-tooltip {
                        left: 50% !important;
                        right: auto !important;
                        transform: translate(-50%, 10px) !important;
                      }

                      .compose-email-editor .ql-tooltip.ql-flip {
                        left: 50% !important;
                        right: auto !important;
                        transform: translate(-50%, -10px) !important;
                      }
                    `}
                  </style>

                  <div className="border-t border-slate-100 px-4 py-3">
                    <div className="custom-control custom-control-right custom-switch ml-1">
                      <input
                        type="checkbox"
                        className="custom-control-input cursor-pointer"
                        id="customSwitch1-emailComposer"
                        checked={includeSignature}
                        onChange={(e) => {
                          setIncludeSignature(e.target.checked);
                          adjustSignature();
                        }}
                      />
                      <label
                        className="custom-control-label cursor-pointer"
                        htmlFor="customSwitch1-emailComposer"
                      >
                        <p className="text-sm text-gray-500">Include Signature?</p>
                      </label>
                    </div>
                    {includeSignature && (
                      <div ref={signatureRef} dangerouslySetInnerHTML={{ __html: signatureHtml }} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence>
            {openDragDrop && (
              <Attachments
                key={`dragDrop-${attachmentModalSource}`}
                setOpenDragDrop={setOpenDragDrop}
                setBase64Strings={setAttachments}
                setHasAttachment={setHasAttachment}
                projectID={projectID}
                initialSource={attachmentModalSource}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ComposeNewEmail;